import * as React from 'react'

// import DeliverySvg from '@/assets/food/icons/delivery.svg?react'
import LocationSvg from '@/assets/food/icons/location.svg?react'
// import StoreSvg from '@/assets/food/icons/store.svg?react'
import ArrowDownSvg from '@/assets/icons/chevron-down.svg?react'
import CloseSvg from '@/assets/icons/close.svg?react'
import SearchSvg from '@/assets/icons/search.svg?react'
import StoreCard from '@/components/StoreCard/StoreCard'
import StoreCardPlaceholder from '@/components/StoreCard/StoreCardPlaceholder'
import useNumberOfItemsPerRow from '@/hooks/useNumberOfItemsPerRow'
import type {SearchInputControl} from '@/layout/FoodInputWithAutocomplete'
import FoodInputWithAutocomplete from '@/layout/FoodInputWithAutocomplete'
import useGetStores from '@/lib/food/hooks/useGetStore'
import {FoodSearchFocus, FoodSort, FoodStoreType} from '@/lib/food/types'
import {FoodRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import BetterSuspense from '@/utils/components/BetterSuspense'
import StoreSorter from '@/views/Food/components/StoreSorter/StoreSorter'

import style from './FoodSearchResult.module.scss'

const FoodSearchResultPagePlaceholder = memo(function FoodSearchResultPagePlaceholder() {
  const numberOfItemsPerRow = useNumberOfItemsPerRow()

  const dummy = new Array(numberOfItemsPerRow).fill(undefined)

  return (
    <div className={style.PageSearchResultContainer}>
      {dummy.map((_, idx) => (
        <div key={`store-placeholder-item-${idx.toString()}`}>
          <StoreCardPlaceholder />
        </div>
      ))}
    </div>
  )
})

const EmptySearchResult = () => (
  <div className={style.PageSearchResultEmpty} role='alert'>No results match your filter.</div>
)

export default memo(function FoodSearchResult() {
  const searchControl = useRef<SearchInputControl>(null)

  const {
    lng,
    lat,
    address,
    user_street_num,
    user_street_name,
    user_city,
    user_state,
    user_zipcode,
    user_country,
  } = FoodRoute.useSearch()

  //FILTERS
  const [filterInput, setFilterInput] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [sorter, setSorter] = useState<FoodSort>(FoodSort.Relevance)
  // const [storeServiceType, setStoreServiceType] = useState(false)
  const [storeAvailability, setStoreAvailability] = useState(true)

  const filters = useMemo(
    () => ({
      query: filterInput,
      longitude: lng,
      latitude: lat,
      store_type: FoodStoreType[''],
      user_street_num,
      user_street_name,
      user_city,
      user_state,
      user_zipcode,
      user_country,
      // pickup: storeServiceType,
      pickup: false,
      fetch_quotes: false,
      sort: sorter,
      open: storeAvailability,
      search_focus: FoodSearchFocus[''],
      use_new_db: true,
    }),
    [
      filterInput,
      lng,
      lat,
      user_street_num,
      user_street_name,
      user_city,
      user_state,
      user_zipcode,
      user_country,
      // storeServiceType,
      storeAvailability,
      sorter,
    ],
  )

  const {data: storeResultData, isLoading: getStoreLoading} = useGetStores(filters)
  const hasStoreResults = storeResultData && storeResultData.stores.length > 0

  const [isNewAddress, setIsNewAddress] = useState(false)

  const handleSearchSubmit = useCallback(() => {
    searchControl.current?.submit()
    setIsNewAddress(false)
  }, [searchControl])

  const handleFilterByName = () => {
    setFilterInput(inputValue)
  }

  return (
    <div className={style.PageContainer}>
      <div className={style.PageContent}>
        <div className={style.PageFilterContainer}>
          <div className={style.PageFilterContent}>
            <div className={style.FilterGroup}>
              {/* <div className={style.FilterList}>
                <button
                  type='button'
                  onClick={() => {
                    setStoreServiceType(false)
                  }}
                  className={clsx(storeServiceType ? style.FilterItem : style.FilterItemActive)}
                >
                  <DeliverySvg className={style.FilterItemIconFill} />
                  <p className={style.FilterItemTitle}>Delivery</p>
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setStoreServiceType(true)
                  }}
                  className={clsx(storeServiceType ? style.FilterItemActive : style.FilterItem)}
                >
                  <StoreSvg className={style.FilterItemIcon} />
                  <p className={style.FilterItemTitle}>Pickup</p>
                </button>
              </div> */}
              <div className={style.FilterList}>
                <button
                  type='button'
                  onClick={() => {
                    setStoreAvailability(true)
                  }}
                  className={clsx(storeAvailability ? style.FilterItemActive : style.FilterItem)}
                  aria-pressed={storeAvailability}
                >
                  <p className={style.FilterItemTitle}>Open only</p>
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setStoreAvailability(false)
                  }}
                  className={clsx(storeAvailability ? style.FilterItem : style.FilterItemActive)}
                  aria-pressed={!storeAvailability}
                >
                  <p className={style.FilterItemTitle}>All shop</p>
                </button>
              </div>
            </div>
            <div className={style.FilterBreakLine}></div>
            <div className={clsx(style.FilterGroup, style.Column)}>
              <div className={style.AddressContainer}>
                <VisuallyHidden role='heading' aria-level={1}>
                  Deliver to: {address}
                </VisuallyHidden>
                <p className={style.Heading} aria-hidden='true'>Deliver to:</p>
                <div className={style.AddressInfo}>
                  <LocationSvg className={style.AddressInfoIcon} />
                  {/* <p className={style.AddressInfoHeading}>Current Location</p> */}
                  <div className={style.AddressInfoValue}>
                    <VisuallyHidden>Current address: </VisuallyHidden>
                    {address}
                  </div>
                  <button
                    type='button'
                    aria-label='Change your address'
                    className={style.AddressChangeBtn}
                    onClick={() => {
                      setIsNewAddress(prev => !prev)
                      searchControl.current?.focus()
                    }}
                  >
                    <ArrowDownSvg
                      className={clsx(
                        style.AddressChangeIcon,
                        isNewAddress && style.AddressChangeIconActive,
                      )}
                    />
                  </button>
                </div>
              </div>
              {isNewAddress && (
                <div className={style.BoxAdressChange}>
                  <BetterSuspense fallback={<div aria-hidden='true' />}>
                    <FoodInputWithAutocomplete
                      control={searchControl}
                      placeholderText='Enter your new delivery address'
                      setResetInput={setIsNewAddress}
                    />
                  </BetterSuspense>
                  <button
                    className={style.BoxAddressChangeButton}
                    type='button'
                    aria-label='Search your new address'
                    onClick={handleSearchSubmit}
                  >
                    Search
                  </button>
                </div>
              )}
              <div className={style.SearchContainer}>
                <div className={style.SearchForm}>
                  <SearchSvg className={style.SearchFormIcon} />
                  <input
                    value={inputValue}
                    onChange={e => {
                      setInputValue(e.target.value)
                    }}
                    type='text'
                    placeholder='Food, grocery, anything'
                    className={style.SearchInput}
                  />
                  {inputValue.length > 0 && (
                    <CloseSvg
                      onClick={() => {
                        setInputValue('')
                        setFilterInput('')
                      }}
                      className={style.SearchFormIcon}
                    />
                  )}
                </div>
                <button
                  type='button'
                  aria-label='Search store by name'
                  onClick={handleFilterByName}
                  className={style.SearchButton}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
          <div className={style.PageFilterSort}>
            {storeResultData?.stores ? (
              <div className={style.TotalResult}>
                <VisuallyHidden role='alert'>{storeResultData.stores.length} results</VisuallyHidden>
              </div>
            ) : (
              <div></div>
            )}
            <div className={style.FilterSort}>
              <StoreSorter sorter={sorter} setSorter={setSorter} />
            </div>
          </div>
        </div>
        {getStoreLoading ? (
          <FoodSearchResultPagePlaceholder />
        ) : (
          <>
            {hasStoreResults ? (
              <div role='listitem' className={style.PageSearchResultContainer}>
                {storeResultData.stores.map(store => {
                  const transformedStore = {
                    ...store,
                    hours: {
                      operational: {
                        Monday: '',
                        Tuesday: '',
                        Wednesday: '',
                        Thursday: '',
                        Friday: '',
                        Saturday: '',
                        Sunday: '',
                        ...store.hours.operational,
                      },
                      delivery: {
                        Monday: '',
                        Tuesday: '',
                        Wednesday: '',
                        Thursday: '',
                        Friday: '',
                        Saturday: '',
                        Sunday: '',
                        ...store.hours.delivery,
                      },
                      pickup: {
                        Monday: '',
                        Tuesday: '',
                        Wednesday: '',
                        Thursday: '',
                        Friday: '',
                        Saturday: '',
                        Sunday: '',
                        ...store.hours.pickup,
                      },
                      dine_in: {
                        Monday: '',
                        Tuesday: '',
                        Wednesday: '',
                        Thursday: '',
                        Friday: '',
                        Saturday: '',
                        Sunday: '',
                        ...store.hours.dine_in,
                      },
                    },
                  }

                  return (
                    <StoreCard
                      key={`store-${store._id}`}
                      store={transformedStore}
                      headingLevel={2}
                      userInfo={{
                        lng,
                        lat,
                        address,
                        user_street_num,
                        user_street_name,
                        user_city,
                        user_state,
                        user_zipcode,
                        user_country,
                      }}
                    />
                  )
                })}
              </div>
            ) : (
              <EmptySearchResult />
            )}
          </>
        )}
      </div>
    </div>
  )
})
